/**
 * 时长处理工具函数
 * 从 ManagePages.tsx 提取，用于处理各种时长格式
 */

/**
 * 解析 ISO 8601 时长格式
 * @param isoDuration - ISO 8601 格式的时长字符串（如 PT1H30M15S）
 * @returns 格式化的时长字符串（如 1:30:15）或 null
 * 
 * @example
 * parseISO8601Duration('PT1H30M15S') // 返回 '1:30:15'
 * parseISO8601Duration('PT28M2S') // 返回 '28:02'
 * parseISO8601Duration('PT45S') // 返回 '0:45'
 */
export const parseISO8601Duration = (isoDuration: string): string | null => {
  if (!isoDuration || !isoDuration.startsWith('PT')) return null;
  
  try {
    const matches = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!matches) return null;
    
    const hours = parseInt(matches[1] || '0', 10);
    const minutes = parseInt(matches[2] || '0', 10);
    const seconds = parseInt(matches[3] || '0', 10);
    
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
  } catch (error) {
    console.warn('⚠️ 无法解析 ISO 8601 duration:', isoDuration, error);
    return null;
  }
};

/**
 * 将秒数转换为可读的时长格式
 * @param seconds - 秒数
 * @returns 格式化的时长字符串
 * 
 * @example
 * secondsToReadableDuration(3665) // 返回 '1:01:05'
 * secondsToReadableDuration(125) // 返回 '2:05'
 */
export const secondsToReadableDuration = (seconds: number): string => {
  if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
    return '0:00';
  }
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  } else {
    return `${minutes}:${String(secs).padStart(2, '0')}`;
  }
};

/**
 * 格式化观看次数（使用 K、M 简写）
 * @param count - 观看次数
 * @returns 格式化的字符串
 * 
 * @example
 * formatViewCount(1500) // 返回 '1.5K'
 * formatViewCount(2500000) // 返回 '2.5M'
 */
export const formatViewCount = (count?: string | number): string => {
  if (!count) return '-';
  const num = typeof count === 'number' ? count : parseInt(count);
  
  if (isNaN(num)) return '-';
  
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

/**
 * 格式化日期字符串
 * @param dateStr - 日期字符串或 Date 对象
 * @returns 格式化的日期字符串（zh-TW 格式）
 * 
 * @example
 * formatDate('2025-11-02') // 返回 '2025/11/2'
 */
export const formatDate = (dateStr?: string | Date): string => {
  if (!dateStr) return '-';
  
  try {
    const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('zh-TW');
  } catch {
    return '-';
  }
};

/**
 * 计算最近添加的数量（过去 7 天）
 * @param items - 包含 addedDate 字段的项目数组
 * @returns 最近 7 天添加的数量
 */
export const getRecentlyAddedCount = (items: Array<{ addedDate?: string | Date }>): number => {
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  return items.filter(item => {
    if (!item.addedDate) return false;
    
    try {
      const addedDate = item.addedDate instanceof Date ? item.addedDate : new Date(item.addedDate);
      return addedDate > oneWeekAgo;
    } catch {
      return false;
    }
  }).length;
};

